// ─── Filter framework: the Tickets descriptor (f-02062) ───────────
// listId 'tickets' — the first (and for now only) adopter. Every field reads
// PCP's LOCAL mirror (zoho_tickets + its threads / comments / per-user review
// overlay); no value source calls Zoho live. The one field the mirror lacked,
// Team, is added to the ticket sync (zoho_tickets.team_id + the team-name
// vocabulary in settings 'zoho:teams'); it fills as the owner runs a
// back-in-time sync manually.
//
// Column map for the SQL registry (apps/api/src/services/filters/entities/tickets.ts):
//   status       status (categories from status_type: Open / On Hold / Closed)
//   department   department_id          (names: settings 'zoho:departments')
//   assignee     assignee_id            (Me = the viewer's Zoho agent id)
//   team         team_id  NEW           (names: settings 'zoho:teams')
//   priority     priority               channel     channel
//   tags         tags (jsonb array)     account     account_id (label account_name)
//   contact      contact_name           email       contact_email
//   subject      subject                ticketNumber ticket_number
//   category     category (AI type)     sentiment   sentiment
//   escalated    escalated              reviewed    user_item_reviews overlay (per viewer)
//   knowledge    extracted image / attachment text on any thread or comment
//   created      zoho_created_at        modified    zoho_modified_at
//   closed       closed_at              threads     thread_count   comments  comment_count
import { FILTER_SCHEMA_VERSION, filterDefinitionOf, makeFilterCondition } from './model.js';
import { BAR_PAGE_SIZES } from './layout.js';
export const TICKETS_LIST_ID = 'tickets';
/** Feature flag the web passes when the viewer's auto-review is on for Zoho:
 *  quick:reviewed and std:unreviewed are then hidden (never deleted). */
export const TICKETS_FEATURE_AUTO_REVIEW = 'autoReview';
/** Status categories (in_category values) and the statusType each maps to. */
export const TICKET_STATUS_CATEGORIES = [
    { key: 'open', label: 'Open', statusType: 'Open', icon: 'circle-dot' },
    { key: 'on-hold', label: 'On hold', statusType: 'On Hold', icon: 'circle-pause' },
    { key: 'closed', label: 'Closed', statusType: 'Closed', icon: 'circle-check' },
];
/** statusType ('Open' | 'On Hold' | 'Closed') → category key. */
export function ticketStatusCategoryOf(statusType) {
    const s = String(statusType ?? '').trim().toLowerCase();
    const hit = TICKET_STATUS_CATEGORIES.find((c) => c.statusType.toLowerCase() === s);
    return hit ? hit.key : null;
}
export const TICKET_SORTS = [
    { key: 'recent', label: 'Recently updated' },
    { key: 'oldest', label: 'Least recently updated' },
    { key: 'created-desc', label: 'Newest created' },
    { key: 'created-asc', label: 'Oldest created' },
    { key: 'priority', label: 'Priority' },
    { key: 'status', label: 'Status' },
];
const ENUM_OPS = ['is', 'is_not', 'any_of', 'none_of'];
const ENUM_EMPTY_OPS = ['is', 'is_not', 'any_of', 'none_of', 'is_empty', 'is_not_empty'];
const TEXT_OPS = ['contains', 'not_contains', 'is', 'is_empty', 'is_not_empty'];
const DATE_OPS = ['before', 'after', 'on', 'between', 'within_last', 'older_than'];
const NUMBER_OPS = ['eq', 'gt', 'lt', 'between'];
const BOOL_OPS = ['is_true', 'is_false'];
export const TICKET_FILTER_FIELDS = [
    {
        key: 'status', label: 'Status', type: 'enum', icon: 'circle-dot',
        ops: ['is', 'is_not', 'any_of', 'none_of', 'in_category'],
        values: 'facet',
        categories: TICKET_STATUS_CATEGORIES.map((c) => ({ key: c.key, label: c.label, icon: c.icon })),
        quick: { control: 'menu', overflowPriority: 90, defaultOp: 'in_category' },
        description: 'Status categories (Open / On hold / Closed) above the desk’s own statuses.',
    },
    {
        key: 'department', label: 'Department', type: 'enum', icon: 'building-2',
        ops: ENUM_OPS, values: 'facet',
        quick: { control: 'menu', overflowPriority: 70, defaultOp: 'any_of' },
    },
    {
        key: 'assignee', label: 'Assignee', type: 'person', icon: 'user',
        tokens: ['me', 'empty'], tokenIcons: { me: 'user-check', empty: 'user-x' }, emptyLabel: 'Unassigned',
        ops: ENUM_EMPTY_OPS, values: 'facet', searchableValues: true,
        quick: { control: 'menu', overflowPriority: 80, defaultOp: 'any_of' },
        description: 'Me is whoever views the list.',
    },
    {
        key: 'team', label: 'Team', type: 'enum', icon: 'users',
        tokens: ['empty'], emptyLabel: 'No team',
        ops: ENUM_EMPTY_OPS, values: 'facet',
        quick: { control: 'menu', overflowPriority: 45, defaultOp: 'any_of' },
        sync: {
            column: 'zoho_tickets.team_id',
            note: 'Added to the Zoho ticket sync for the filter framework; team names are stored in settings zoho:teams. Older tickets fill on a back-in-time sync.',
        },
    },
    {
        key: 'priority', label: 'Priority', type: 'enum', icon: 'flag',
        tokens: ['empty'], emptyLabel: 'No priority',
        ops: ENUM_EMPTY_OPS, values: 'facet',
        quick: { control: 'menu', overflowPriority: 50, defaultOp: 'any_of' },
    },
    {
        key: 'channel', label: 'Channel', type: 'enum', icon: 'at-sign',
        ops: ENUM_OPS, values: 'facet',
        quick: { control: 'menu', overflowPriority: 35, defaultOp: 'any_of' },
    },
    {
        key: 'tags', label: 'Tags', type: 'multi', icon: 'tag',
        ops: ['has_any', 'has_all', 'has_none', 'is_empty', 'is_not_empty'], values: 'facet', searchableValues: true,
        quick: { control: 'menu', overflowPriority: 30, defaultOp: 'has_any' },
    },
    {
        key: 'account', label: 'Account', type: 'enum', icon: 'landmark',
        tokens: ['empty'], emptyLabel: 'No account',
        ops: ENUM_EMPTY_OPS, values: 'facet', searchableValues: true,
        quick: { control: 'menu', overflowPriority: 25, defaultOp: 'any_of' },
    },
    {
        key: 'contact', label: 'Contact', type: 'text', icon: 'contact',
        ops: TEXT_OPS, values: 'none',
    },
    {
        key: 'email', label: 'Contact email', type: 'text', icon: 'mail',
        ops: TEXT_OPS, values: 'none',
    },
    {
        key: 'subject', label: 'Subject', type: 'text', icon: 'file-text',
        ops: ['contains', 'not_contains'], values: 'none',
    },
    {
        key: 'ticketNumber', label: 'Ticket #', type: 'text', icon: 'hash',
        ops: ['is', 'contains'], values: 'none',
    },
    {
        key: 'category', label: 'Ticket type', type: 'enum', icon: 'layers',
        tokens: ['empty'], emptyLabel: 'Not classified',
        ops: ENUM_EMPTY_OPS, values: 'static',
        staticValues: [
            { value: 'error', label: 'Error', icon: 'bug' },
            { value: 'how-to', label: 'How-to', icon: 'lightbulb' },
            { value: 'case-handling', label: 'Case handling', icon: 'clipboard-list' },
            { value: 'other', label: 'Other', icon: 'circle' },
        ],
        quick: { control: 'menu', overflowPriority: 25, defaultOp: 'any_of' },
        description: 'The AI-derived ticket type.',
    },
    {
        key: 'sentiment', label: 'Sentiment', type: 'enum', icon: 'heart',
        tokens: ['empty'], emptyLabel: 'Not scored',
        ops: ENUM_EMPTY_OPS, values: 'static',
        staticValues: [
            { value: 'positive', label: 'Positive', icon: 'heart' },
            { value: 'neutral', label: 'Neutral', icon: 'circle' },
            { value: 'negative', label: 'Negative', icon: 'thumbs-down' },
        ],
        quick: { control: 'menu', overflowPriority: 20, defaultOp: 'any_of' },
    },
    {
        key: 'escalated', label: 'Escalated', type: 'bool', icon: 'siren',
        ops: BOOL_OPS, values: 'none',
        quick: { control: 'toggle', overflowPriority: 40, defaultOp: 'is_true' },
    },
    {
        key: 'reviewed', label: 'Reviewed', type: 'bool', icon: 'check-check',
        ops: BOOL_OPS, values: 'none', viewerRelative: true,
        availability: { hiddenWhenFeature: TICKETS_FEATURE_AUTO_REVIEW },
        quick: { control: 'menu', overflowPriority: 60, defaultOp: 'is_false' },
        description: 'Your own review state — results depend on who views it.',
    },
    {
        key: 'knowledge', label: 'Attachment knowledge', type: 'bool', icon: 'paperclip',
        ops: BOOL_OPS, values: 'none',
        quick: { control: 'toggle', overflowPriority: 55, defaultOp: 'is_true' },
        description: 'Has extracted image or attachment text on a message.',
    },
    {
        key: 'created', label: 'Created', type: 'date', icon: 'calendar-days',
        ops: DATE_OPS, values: 'none',
        quick: { control: 'date', overflowPriority: 15, defaultOp: 'within_last' },
    },
    {
        key: 'modified', label: 'Updated', type: 'date', icon: 'calendar-clock',
        ops: DATE_OPS, values: 'none',
        quick: { control: 'date', overflowPriority: 15, defaultOp: 'within_last' },
    },
    {
        key: 'closed', label: 'Closed', type: 'date', icon: 'calendar-check',
        ops: [...DATE_OPS, 'is_empty', 'is_not_empty'], values: 'none',
        quick: { control: 'date', overflowPriority: 10, defaultOp: 'within_last' },
    },
    {
        key: 'threads', label: 'Threads', type: 'number', icon: 'messages-square',
        ops: NUMBER_OPS, values: 'none',
    },
    {
        key: 'comments', label: 'Comments', type: 'number', icon: 'message-circle',
        ops: NUMBER_OPS, values: 'none',
    },
];
export const TICKET_FILTER_FIELD_KEYS = TICKET_FILTER_FIELDS.map((f) => f.key);
const cond = (id, field, op, values = []) => makeFilterCondition(field, op, values, id);
const def = (...conds) => ({
    schema: FILTER_SCHEMA_VERSION,
    root: { kind: 'group', id: 'root', conj: 'and', children: conds },
});
export const TICKET_STANDARD_FILTERS = [
    { key: 'all', name: 'All tickets', icon: 'inbox', definition: def() },
    {
        key: 'my-open', name: 'My open tickets', icon: 'user-check',
        definition: def(cond('c1', 'assignee', 'is', [{ token: 'me' }]), cond('c2', 'status', 'in_category', ['open'])),
    },
    {
        key: 'unassigned-open', name: 'Unassigned open', icon: 'user-x',
        definition: def(cond('c1', 'assignee', 'is_empty'), cond('c2', 'status', 'in_category', ['open'])),
    },
    { key: 'on-hold', name: 'On hold', icon: 'circle-pause', definition: def(cond('c1', 'status', 'in_category', ['on-hold'])) },
    {
        key: 'unreviewed', name: 'Unreviewed', icon: 'eye',
        definition: def(cond('c1', 'reviewed', 'is_false')),
        availability: { hiddenWhenFeature: TICKETS_FEATURE_AUTO_REVIEW },
    },
];
const QUICK_ITEMS = TICKET_FILTER_FIELDS.filter((f) => f.quick).map((f) => `quick:${f.key}`);
export const TICKETS_FILTER_LIST = {
    listId: TICKETS_LIST_ID,
    noun: { one: 'ticket', many: 'tickets' },
    allLabel: 'All tickets',
    searchPlaceholder: 'Search subject, contact, ticket #…',
    mode: 'server',
    fields: TICKET_FILTER_FIELDS,
    sorts: TICKET_SORTS,
    defaultSort: 'recent',
    standardFilters: TICKET_STANDARD_FILTERS,
    bar: {
        ariaLabel: 'Ticket filters',
        defaultLayout: {
            items: [
                'filtersMenu', 'pinnedFilters', 'search', 'space#1',
                'quick:status', 'quick:department', 'quick:assignee', 'quick:reviewed', 'quick:knowledge',
                'addFilter', 'flexibleSpace#1', 'saveState', 'count', 'sort',
            ],
            pins: [],
            tabs: [],
            searchPlacement: 'inline',
            tabsPlacement: 'above',
            displayMode: 'auto',
            pageSize: 25,
        },
        palette: [
            'filtersMenu', 'pinnedFilters', 'search', 'addFilter', 'advanced', 'saveState', 'count', 'sort', 'clear', 'customize',
            ...QUICK_ITEMS,
            'space', 'flexibleSpace', 'divider',
        ],
        movableButNotRemovable: ['filtersMenu', 'search', 'pinnedFilters'],
        overflowPriority: {
            search: 100,
            sort: 95,
            pinnedFilters: 85,
            addFilter: 75,
            // Renders nothing on a clean view; on an Edited one Save / Discard is the
            // primary action, so it outlasts every quick filter.
            saveState: 92,
            advanced: 30,
            clear: 25,
            count: 20,
            customize: 10,
        },
    },
};
const asList = (v) => (v === undefined ? [] : Array.isArray(v) ? v : [v]).map(String).filter(Boolean);
/**
 * The definition equivalent to the legacy list params (and the old
 * zoho-list-settings): department[] → department any_of, statusType →
 * status in_category, reviewed → reviewed is_true / is_false,
 * hasAttachments → knowledge is_true, priority[] → priority any_of.
 * Used by the one-shot migration and the server's legacy-parity test.
 */
export function ticketLegacyParamsToDefinition(p) {
    const conds = [];
    const depts = asList(p.department);
    if (depts.length)
        conds.push(makeFilterCondition('department', depts.length === 1 ? 'is' : 'any_of', depts));
    const st = String(p.statusType ?? 'all');
    if (st === 'open' || st === 'on-hold' || st === 'closed')
        conds.push(makeFilterCondition('status', 'in_category', [st]));
    const rev = String(p.reviewed ?? 'all');
    if (rev === 'reviewed')
        conds.push(makeFilterCondition('reviewed', 'is_true'));
    else if (rev === 'unreviewed')
        conds.push(makeFilterCondition('reviewed', 'is_false'));
    if (p.hasAttachments === true || p.hasAttachments === '1' || p.hasAttachments === 'true')
        conds.push(makeFilterCondition('knowledge', 'is_true'));
    const prios = asList(p.priority);
    if (prios.length)
        conds.push(makeFilterCondition('priority', prios.length === 1 ? 'is' : 'any_of', prios));
    return filterDefinitionOf(conds);
}
/** Map the old localStorage 'zoho-list-settings' JSON (read once, applied as
 *  an UNSAVED draft, then the key is deleted). Null when there is nothing. */
export function migrateLegacyTicketListSettings(raw) {
    let obj = null;
    if (typeof raw === 'string') {
        try {
            obj = JSON.parse(raw);
        }
        catch {
            return null;
        }
    }
    else if (raw && typeof raw === 'object')
        obj = raw;
    if (!obj || typeof obj !== 'object' || Array.isArray(obj))
        return null;
    const definition = ticketLegacyParamsToDefinition({
        department: Array.isArray(obj.department) ? obj.department.filter((x) => typeof x === 'string') : undefined,
        statusType: typeof obj.statusType === 'string' ? obj.statusType : undefined,
        reviewed: typeof obj.reviewed === 'string' ? obj.reviewed : undefined,
        hasAttachments: obj.hasAttachments === true,
    });
    const search = typeof obj.q === 'string' ? obj.q.trim() : '';
    const sort = TICKET_SORTS.some((s) => s.key === obj.sort) ? obj.sort : 'recent';
    const pageSize = BAR_PAGE_SIZES.includes(obj.pageSize) ? obj.pageSize : null;
    const meaningful = definition.root.children.length > 0 || search !== '' || sort !== 'recent' || (pageSize !== null && pageSize !== 25);
    return { definition, search, sort, pageSize, meaningful };
}
//# sourceMappingURL=tickets.js.map